import { Component, Inject, OnInit, inject, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { RouterModule } from "@angular/router";
import { MatCardModule } from "@angular/material/card";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatChipsModule } from "@angular/material/chips";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { MAT_DIALOG_DATA } from "@angular/material/dialog";
import { firstValueFrom } from "rxjs";

import { Cohort, CohortStatus, MemberCohort } from "../../../models/cohort.model";
import { CohortService } from "../../../services/cohort.service";

@Component({
	selector: "app-cohorts-list",
	standalone: true,
	imports: [
		CommonModule,
		RouterModule,
		MatCardModule,
		MatButtonModule,
		MatIconModule,
		MatChipsModule,
		MatProgressBarModule,
		MatProgressSpinnerModule,
		MatDialogModule,
	],
	templateUrl: "./cohorts-list.component.html",
	styleUrls: ["./cohorts-list.component.scss"],
})
export class CohortsListComponent implements OnInit {
	private cohortService = inject(CohortService);
	private snackBar = inject(MatSnackBar);
	private dialog = inject(MatDialog);

	memberCohorts = signal<MemberCohort[]>([]);
	loading = signal(true);

	async ngOnInit() {
		await this.loadCohorts();
	}

	private async loadCohorts() {
		try {
			this.loading.set(true);

			// Get active cohorts for the current member
			const cohorts = await firstValueFrom(this.cohortService.getActiveMemberCohorts());
			this.memberCohorts.set(cohorts);
		} catch (error) {
			this.snackBar.open("Error loading cohorts", "Close", { duration: 3000 });
			console.error("Error loading cohorts:", error);
		} finally {
			this.loading.set(false);
		}
	}

	getStatusColor(status: string): string {
		switch (status) {
			case "completed":
				return "accent";
			case "in_progress":
				return "primary";
			case "pending":
				return "warn";
			default:
				return "primary";
		}
	}

	getStatusText(status: string): string {
		switch (status) {
			case "completed":
				return "Completed";
			case "in_progress":
				return "In Progress";
			case "pending":
				return "Pending";
			default:
				return status;
		}
	}

	// Helper method to handle Firestore timestamps
	formatDate(timestamp: any): Date | null {
		if (!timestamp) return null;

		// Check if it's a Firestore timestamp
		if (timestamp && typeof timestamp.toDate === "function") {
			return timestamp.toDate();
		}

		// If it's already a Date object
		if (timestamp instanceof Date) {
			return timestamp;
		}

		// If it's a string or number, try to create a Date
		try {
			return new Date(timestamp);
		} catch {
			return null;
		}
	}

	async deleteCohort(id: string) {
		try {
			await firstValueFrom(this.cohortService.deleteCohort(id));
			await this.loadCohorts();
			this.snackBar.open("Survey deleted successfully", "Close", {
				duration: 3000,
			});
		} catch (error) {
			this.snackBar.open("Error deleting survey", "Close", { duration: 3000 });
			console.error("Error deleting survey:", error);
		}
	}

	openDeleteDialog(cohort: MemberCohort) {
		const dialogRef = this.dialog.open(DeleteCohortDialogComponent, {
			width: "400px",
			data: { cohort },
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				this.deleteCohort(cohort.id!);
			}
		});
	}
}

// Delete confirmation dialog component
@Component({
	selector: "app-delete-cohort-dialog",
	standalone: true,
	imports: [CommonModule, MatDialogModule, MatButtonModule],
	template: `
		<h2 mat-dialog-title>Delete Survey</h2>
		<mat-dialog-content>
			<p>Are you sure you want to delete this survey?</p>
			<p>
				<strong>{{ data.cohort.cohort_name }}</strong>
			</p>
		</mat-dialog-content>
		<mat-dialog-actions align="end">
			<button mat-button mat-dialog-close>Cancel</button>
			<button mat-raised-button color="warn" [mat-dialog-close]="true">Delete</button>
		</mat-dialog-actions>
	`,
})
export class DeleteCohortDialogComponent {
	constructor(@Inject(MAT_DIALOG_DATA) public data: any) {}
}
